import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import {
  ahuBoardApi,
  refetchSectionsAfterTunnelResize,
  useBulkUpdateAhuTunnelsMutation,
  usePatchAhuTunnelMutation,
} from 'app/services/ahuBoardApi';
import {
  ahuBoardFormsApi,
  useBulkUpdateSectionsMutation,
  usePatchAhuFloorMutation,
} from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardSection, IAhuBoardTunnel, IAhuBoardUnit, ICmsAhuDoc } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { refetchHybridLugPlacements } from '../../boardActions';
import PaginatedSelect from '../../forms/PaginatedSelect';
import { validateTunnel } from '../../validation/validationController';
import { cmsBaseList, cmsWidthValues, cmsHeightValues } from '../../forms/cmsDoc';
import { catalogLabel, formatLegacyNumber } from '../../forms/formUtils';

/**
 * Phase 6e1 — multi-section bulk panel (§7.4.3, tab.multiply.sections.tmpl,
 * markup faithful): 4 paginated selects with common-value resolution
 * ("Varies" for mixed → null shown), change → POST bulk_update →
 * revalidate touched tunnels + local cache patch + `change:casing:section_<id>`
 * per section (liner/floor fields) + `sections:bulk_update` for per-section
 * select sync.
 *
 * Hybrid AHUs additionally get Height/Width, Base, and Floor
 * Under-Insulation/Under-Liner (§7.4.3 "Add bulk Tunnel Height/Width edit" +
 * "Add Base and Floor Under-Insulation/Under-Liner"). These are TUNNEL (or
 * tunnel-floor) columns, not section columns, so they resolve to the
 * DISTINCT tunnels the selected sections belong to rather than the section
 * list itself, and PATCH through the tunnel/floor endpoints instead of the
 * section bulk_update endpoint.
 *
 * Height, Width and Base are each a plain native <select> borrowing
 * PaginatedSelectView's `.custom-select-with-pagination` / `.selected` box (the
 * `ka-native-select` variant — no paginated API backs a static CMS list). That
 * box is not decoration: `.inputs-group .sublist > li div select` (styles.css)
 * paints a Data Bar select's own text transparent, so a bare one renders an
 * EMPTY row whatever it has selected.
 *
 * Height and Width are two SEPARATE full-width rows, exactly as legacy's
 * tab.multiply.sections.tmpl renders them. They are NOT the AHU tab's combined
 * `.multiply-total.multiply.multiply-tunel` W×H×L strip: that row is unlabelled
 * and its `.multiply` rules size `.small-input` to 69px, which in a labelled
 * data-bar row leaves the controls half-width and misaligned against the
 * selects above.
 */

/**
 * W/H options are CMS value NAMES ("100.00" — value_names is "{:.2f}") while
 * tunnel.width/height are floats (100), so the native select's selected option
 * has to be matched numerically (same helper as TunnelCabinetRow; legacy's
 * `{{#ifCompare this '==' ../model.height}}` is a loose compare).
 */
const matchedDim = (values: Array<string | number>, raw: unknown): string => {
  if (raw == null) return '';
  const n = Number(raw);
  const hit = values.find((v) => Number(v) === n);
  return hit != null ? String(hit) : String(raw);
};

const FIELDS = [
  ['casing-insulation-select', 'casing_insulation_id', 'ahu/casing/insulations', 'Casing Insulation'],
  ['casing-innerliner-select', 'casing_innerliner_id', 'ahu/casing/liner_metal_sheets', 'Casing Inner Liner'],
  ['floor-innerliner-select', 'floor_innerliner_id', 'ahu/floor/metal_sheets', 'Floor Deck'],
  ['floor-finish-select', 'floor_finish_id', 'ahu/floor/finishes', 'Floor Deck Finish'],
] as const;

const FLOOR_FIELDS = [
  ['under-insulation-select', 'under_insulation_id', 'ahu/floor/under_insulations', 'Floor Under-Insulation'],
  ['under-liner-select', 'underliner_id', 'ahu/floor/under_liner_metal_sheets', 'Floor Under-Liner'],
] as const;

interface BulkSectionsPanelProps {
  ahu: IAhuBoardUnit;
  tunnels: IAhuBoardTunnel[];
  selectedSections: IAhuBoardSection[];
  cms: ICmsAhuDoc | undefined;
  mfgId: number;
}

const BulkSectionsPanel = ({
  ahu,
  tunnels,
  selectedSections,
  cms,
  mfgId,
}: BulkSectionsPanelProps) => {
  const dispatch = useAppDispatch();
  const [bulkUpdate] = useBulkUpdateSectionsMutation();
  const [bulkUpdateTunnels] = useBulkUpdateAhuTunnelsMutation();
  const [patchTunnel] = usePatchAhuTunnelMutation();
  const [patchFloor] = usePatchAhuFloorMutation();
  const ccid = ahu.construction_class_id ?? null;
  const isHybrid = !!ahu.is_hybrid;

  const sectionIds = selectedSections.map((s) => s.id);
  const distinctTunnels = useMemo(() => {
    const ids = new Set(selectedSections.map((s) => s.tunnel_id));
    return tunnels.filter((t) => ids.has(t.id));
  }, [selectedSections, tunnels]);
  const distinctTunnelIds = distinctTunnels.map((t) => t.id);

  // AHUTunnelFloor is lazy-fetched per tunnel — ensure every distinct
  // tunnel's floor row is loaded (TunnelCabinetRow already loads it for
  // whichever tunnels are open, but the bulk selection can span tunnels
  // whose AHU-tab row has never been expanded this session).
  useEffect(() => {
    if (!isHybrid) return;
    distinctTunnels.forEach((t) => {
      if (t.floor_id != null) {
        dispatch(ahuBoardFormsApi.endpoints.getAhuFloor.initiate(Number(t.floor_id)));
      }
    });
  }, [isHybrid, distinctTunnels, dispatch]);

  const floorByTunnel = useAppSelector((state) => {
    const map: Record<number, Record<string, unknown> | undefined> = {};
    distinctTunnels.forEach((t) => {
      if (t.floor_id != null) {
        map[t.id] = ahuBoardFormsApi.endpoints.getAhuFloor.select(Number(t.floor_id))(state)?.data;
      }
    });
    return map;
  });

  // getCommonValues (legacy tab.multiply.sections.js:80): all-same →
  // firstValue || 'None' (common-falsy keeps the '-----' default text);
  // differing → null → '.selected' shows 'Varies' (renderSelects:182).
  const commonValue = (field: string): { value: number | null; mixed: boolean } => {
    const values = selectedSections.map(
      (s) => (s as Record<string, unknown>)[field] as number | null | undefined
    );
    const first = values[0] ?? null;
    const allSame = values.every((v) => (v ?? null) === first);
    return { value: allSame ? first : null, mixed: !allSame };
  };

  const onBulkChange = (field: string, value: number | null) => {
    bulkUpdate({ section_ids: sectionIds, [field]: value })
      .unwrap()
      .then(() => {
        // Local attribute patch on every touched section row.
        const byTunnel = new Map<number, number[]>();
        selectedSections.forEach((s) => {
          const list = byTunnel.get(s.tunnel_id) ?? [];
          list.push(s.id);
          byTunnel.set(s.tunnel_id, list);
        });
        byTunnel.forEach((ids, tunnelId) => {
          dispatch(
            ahuBoardApi.util.updateQueryData('getTunnelSectionList', tunnelId, (rows) => {
              rows.forEach((row) => {
                if (ids.includes(row.id)) (row as Record<string, unknown>)[field] = value;
              });
            })
          );
          void dispatch(validateTunnel(tunnelId));
        });
        if (field !== 'casing_insulation_id') {
          sectionIds.forEach((id) => ahuBoardBus.emit(`change:casing:section_${id}`));
        }
        ahuBoardBus.emit('sections:bulk_update', sectionIds, field, value);
      })
      .catch(() => undefined);
  };

  // Same "varies" tri-state convention as commonValue above, sourced from
  // the distinct tunnels (or their floor rows) instead of the sections.
  const commonTunnelValue = (
    field: 'height' | 'width' | 'base_id'
  ): { value: number | null; mixed: boolean } => {
    const values = distinctTunnels.map((t) => (t[field] as number | null | undefined) ?? null);
    const first = values[0] ?? null;
    const allSame = values.every((v) => v === first);
    return { value: allSame ? first : null, mixed: !allSame };
  };

  const commonFloorValue = (
    field: 'under_insulation_id' | 'underliner_id'
  ): { value: number | null; mixed: boolean } => {
    const values = distinctTunnels.map((t) => (floorByTunnel[t.id]?.[field] as number | null | undefined) ?? null);
    const first = values[0] ?? null;
    const allSame = values.every((v) => v === first);
    return { value: allSame ? first : null, mixed: !allSame };
  };

  const onHeightWidthChange = (field: 'height' | 'width', value: number) => {
    bulkUpdateTunnels({ ahuId: ahu.id, sectionIds, [field]: value })
      .unwrap()
      .then(() => {
        // The bulk endpoint PATCHes each distinct tunnel server-side, so every
        // touched tunnel's sections need the same aw_width/aw_height re-GET a
        // single-tunnel PATCH gets (refetchSectionsAfterTunnelResize).
        refetchSectionsAfterTunnelResize(dispatch, { [field]: value }, distinctTunnelIds);
        distinctTunnelIds.forEach((tunnelId) => {
          void dispatch(validateTunnel(tunnelId));
          ahuBoardBus.emit('change:tunnel:dimensions', { tunnel_id: tunnelId, render: true });
        });
        // These rows only exist on a hybrid AHU, where each of those PATCHes
        // has already re-laid the lug runs — so re-read the placements rather
        // than hide the lugs and arm ReCalculate (the single-tunnel W/H path
        // takes the same fork; see refetchHybridLugPlacements).
        void dispatch(refetchHybridLugPlacements(ahu.id, { hideWhilePending: true }));
      })
      .catch(() => undefined);
  };

  const onBaseChange = (value: number | null) => {
    Promise.all(
      distinctTunnels.map((t) =>
        patchTunnel({ id: t.id, ahuId: t.ahu_id, data: { base_id: value } }).unwrap()
      )
    )
      .then(() => {
        distinctTunnelIds.forEach((tunnelId) =>
          ahuBoardBus.emit('tunnelLugs:recalcPlacement', { tunnel_id: tunnelId, hide_from_canvas: true })
        );
      })
      .catch(() => undefined);
  };

  const onFloorFieldChange = (field: 'under_insulation_id' | 'underliner_id', value: number | null) => {
    Promise.all(
      distinctTunnels
        .filter((t) => t.floor_id != null)
        .map((t) =>
          patchFloor({
            id: Number(t.floor_id),
            data: { [field]: value, tunnel_id: t.id },
          })
            .unwrap()
            .then(() => ahuBoardBus.emit(`update:tunnel:${Number(t.floor_id)}:fields`))
        )
    )
      .then(() => {
        if (field === 'under_insulation_id') {
          dispatch(ahuBoardApi.util.invalidateTags([{ type: 'AhuTunnels', id: ahu.id }]));
        }
        distinctTunnelIds.forEach((tunnelId) =>
          dispatch(ahuBoardApi.util.invalidateTags([{ type: 'AhuSections', id: tunnelId }]))
        );
      })
      .catch(() => undefined);
  };

  const widths = cmsWidthValues(cms);
  const heights = cmsHeightValues(cms);
  const { value: heightValue, mixed: heightMixed } = commonTunnelValue('height');
  const { value: widthValue, mixed: widthMixed } = commonTunnelValue('width');
  const { value: baseValue, mixed: baseMixed } = commonTunnelValue('base_id');
  const bases = cmsBaseList(cms);
  /**
   * The Base row's closed-state text, in the `.selected` box the header
   * describes — the row was a bare <select> until now, so it drew nothing at
   * all. Three states, the same three every other row of the panel has: the
   * tunnels share a base → its catalogue label; they differ → 'Varies'; none of
   * them has one → '- -'.
   */
  const baseText = baseMixed
    ? 'Varies'
    : catalogLabel(bases.find((b) => Number(b.id) === baseValue)) || '- -';

  return (
    <div className="section-cabinet-list menu-item ahu-left-container">
      <div className="inputs-group">
        <ul className="sublist">
          <li>
            <div className="single-line">
              <span className="label multiply-sctions-label">Multiple Sections</span>
            </div>
          </li>
          {FIELDS.map(([selectId, field, path, label], idx) => {
            const { value, mixed } = commonValue(field);
            return (
              <li key={field} data-select-index={idx}>
                <div className="single-line-with-custom-select">
                  <div id={selectId} className="select-container">
                    <PaginatedSelect
                      mfgId={mfgId}
                      path={path}
                      constructionClassId={ccid}
                      value={value}
                      onChange={(id) => onBulkChange(field, id)}
                      selectName={field}
                      selectClass="multi_section_field"
                      nullText={mixed ? 'Varies' : '-----'}
                    />
                  </div>
                  <span className="label">{label}</span>
                </div>
              </li>
            );
          })}
          {isHybrid && (
            <>
              <li data-select-index="height">
                <div className="single-line-with-custom-select">
                  <div className="select-container">
                    <div className="custom-select-with-pagination ka-native-select">
                      <div className="selected">
                        {heightMixed ? 'Varies' : formatLegacyNumber(heightValue, 0.01)}
                      </div>
                      <select
                        className="multi_tunnel_field required-field"
                        name="height"
                        value={heightMixed ? '' : matchedDim(heights, heightValue)}
                        onChange={(e) => onHeightWidthChange('height', Number(e.currentTarget.value))}
                      >
                        <option value="" disabled>
                          - -
                        </option>
                        {heights.map((h) => (
                          <option key={String(h)} value={String(h)}>
                            {String(h)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <span className="label">Height</span>
                </div>
              </li>
              <li data-select-index="width">
                <div className="single-line-with-custom-select">
                  <div className="select-container">
                    <div className="custom-select-with-pagination ka-native-select">
                      <div className="selected">
                        {widthMixed ? 'Varies' : formatLegacyNumber(widthValue, 0.01)}
                      </div>
                      <select
                        className="multi_tunnel_field required-field"
                        name="width"
                        value={widthMixed ? '' : matchedDim(widths, widthValue)}
                        onChange={(e) => onHeightWidthChange('width', Number(e.currentTarget.value))}
                      >
                        <option value="" disabled>
                          - -
                        </option>
                        {widths.map((w) => (
                          <option key={String(w)} value={String(w)}>
                            {String(w)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <span className="label">Width</span>
                </div>
              </li>
              <li data-select-index="5">
                <div className="single-line-with-custom-select">
                  <div className="select-container">
                    <div className="custom-select-with-pagination ka-native-select">
                      <div className="selected">{baseText}</div>
                      <select
                        className="multi_section_field"
                        name="base_id"
                        value={baseValue ?? ''}
                        onChange={(e) =>
                          onBaseChange(e.currentTarget.value ? Number(e.currentTarget.value) : null)
                        }
                      >
                        {/* Stays '- -' even when the bases differ: clearing the
                            base is a real choice in the open list, and 'Varies'
                            belongs to the closed state (`.selected`) only. */}
                        <option value="">- -</option>
                        {bases.map((b) => (
                          <option key={b.id} value={b.id}>
                            {catalogLabel(b)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <span className="label">Base</span>
                </div>
              </li>
              {FLOOR_FIELDS.map(([selectId, field, path, label], idx) => {
                const { value, mixed } = commonFloorValue(field);
                return (
                  <li key={field} data-select-index={6 + idx}>
                    <div className="single-line-with-custom-select">
                      <div id={selectId} className="select-container">
                        <PaginatedSelect
                          mfgId={mfgId}
                          path={path}
                          constructionClassId={ccid}
                          value={value}
                          onChange={(id) => onFloorFieldChange(field, id)}
                          selectName={field}
                          selectClass="multi_section_field"
                          nullText={mixed ? 'Varies' : '-----'}
                        />
                      </div>
                      <span className="label">{label}</span>
                    </div>
                  </li>
                );
              })}
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

export default BulkSectionsPanel;
